import { useState, useCallback } from 'react';
import { Curriculum, CurriculumCompletion, TrainingRecord } from '../types';
import {
  getCurricula, addCurriculum as apiAddC, updateCurriculum as apiUpdateC, deleteCurriculum as apiDeleteC,
  getCurriculumCompletions, addCurriculumCompletion,
  getTrainingRecords, completeTrainingRecord,
} from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

export function useTraining(companyId: string | null) {
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [completions, setCompletions] = useState<CurriculumCompletion[]>([]);
  const [trainingRecords, setTrainingRecords] = useState<TrainingRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    const [c, cc, tr] = await runAction((cid) => Promise.all([
      getCurricula(cid),
      getCurriculumCompletions(cid),
      getTrainingRecords(cid),
    ]));
    setCurricula(c);
    setCompletions(cc);
    setTrainingRecords(tr);
    return { curricula: c, completions: cc, trainingRecords: tr };
  }, [runAction]);

  const addCurriculum = useCallback(async (c: Curriculum) => {
    const next = await runAction((cid) => apiAddC(c, cid));
    setCurricula(Array.isArray(next) ? next : []);
    return next;
  }, [runAction]);

  const updateCurriculum = useCallback(async (c: Curriculum) => {
    const next = await runAction((cid) => apiUpdateC(c, cid));
    setCurricula(Array.isArray(next) ? next : []);
    return next;
  }, [runAction]);

  const deleteCurriculum = useCallback(async (id: string) => {
    const next = await runAction((cid) => apiDeleteC(id, cid));
    setCurricula(Array.isArray(next) ? next : []);
    return next;
  }, [runAction]);

  const addCompletion = useCallback(async (employeeId: string, curriculumId: string, satisfaction: number) => {
    const next = await runAction((cid) => addCurriculumCompletion(employeeId, curriculumId, satisfaction, cid));
    setCompletions(Array.isArray(next) ? next : []);
    return next;
  }, [runAction]);

  const completeRecord = useCallback(async (employeeId: string, toolId: string, satisfaction: number, proficiency: number) => {
    const updated = await runAction((cid) => completeTrainingRecord(employeeId, toolId, satisfaction, proficiency, cid));
    setTrainingRecords(updated);
    return updated;
  }, [runAction]);

  return {
    curricula, setCurricula,
    completions, setCompletions,
    trainingRecords, setTrainingRecords,
    loading, error,
    load, addCurriculum, updateCurriculum, deleteCurriculum, addCompletion, completeRecord,
  };
}
